import { useMemo } from 'react'
import { GraphQL } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'

import * as Table from '@/components/ui/Table'
import Loading from '@/components/ui/Loading'
import usePaginatedGuests, { QueryVariables } from '../hooks/usePaginatedGuests'
import GuestRow from './GuestRow'
import { UserGroup02Icon } from '@hugeicons/core-solid-rounded'

function LoadingRow() {
  return (
    <Table.Row className="pointer-events-none">
      <Table.Cell colSpan={3}>
        <div className="flex flex-row justify-center p-4">
          <Loading />
        </div>
      </Table.Cell>
    </Table.Row>
  )
}

interface Props {
  variables: QueryVariables
  selectedIds: number[]
  showHeading?: boolean
  onSelect: (guest: GraphQL.GuestFragmentFragment) => void
}

export default function GuestTable(props: Props) {
  const { variables, selectedIds, onSelect, showHeading = false } = props
  const { t } = useLingui()

  const {
    items: guests,
    page,
    metadata,
    loading,
    loadPage
  } = usePaginatedGuests(variables)

  const { totalPages, totalEntries } = useMemo(() => {
    return {
      totalPages: metadata?.totalPages,
      totalEntries: metadata?.totalEntries
    }
  }, [metadata])

  const hasResults = useMemo(
    () => !!totalEntries && totalEntries > 0,
    [totalEntries]
  )

  return (
    <Table.Root>
      {showHeading && (
        <Table.Head>
          <Table.Row>
            <Table.Heading />
            <Table.Heading>
              <Trans>Title</Trans>
            </Table.Heading>
            <Table.Heading>
              <Trans>Name</Trans>
            </Table.Heading>
            <Table.Heading>
              <Trans>Email</Trans>
            </Table.Heading>
          </Table.Row>
        </Table.Head>
      )}
      <Table.Body>
        {guests?.map(guest => {
          return (
            <GuestRow
              key={guest.token}
              guest={guest}
              isSelected={selectedIds.includes(guest.id)}
              onSelect={() => onSelect(guest)}
            />
          )
        })}
        {!hasResults && !loading && (
          <Table.Empty Icon={UserGroup02Icon} text={t`No Guests found`} />
        )}
        {loading && <LoadingRow />}
      </Table.Body>
      {hasResults && page && (
        <Table.PaginationCaption
          totalEntries={totalEntries!}
          currentPage={page}
          totalPages={totalPages!}
          onChangePage={loadPage}
        />
      )}
    </Table.Root>
  )
}
